import React, { useRef, useCallback, useEffect } from 'react'
import { FaPlusSquare, FaLongArrowAltLeft } from 'react-icons/fa'
import cn from 'classnames'
import { html } from 'lib'

import 'components/graph/controls/controls.less'
import 'components/graph/controls/createEdgeControl.less'

type CreateEdgeControlProps = {
  isCreating: boolean
  isEditing: boolean
  disabled: boolean
  onStartCreating: () => void
  onStopCreating: () => void
  control: string
}

// Same drag-to-connect interaction as the (currently hidden) per-node Add control:
// press to arm, drag onto a node to connect it, or onto empty space to create a new node there.
const CreateEdgeControl = ({ isCreating, isEditing, disabled, onStartCreating, onStopCreating, control }: CreateEdgeControlProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const touchStarted = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (isEditing || disabled) return
    e.preventDefault()
    e.stopPropagation()
    if (isCreating) {
      onStopCreating()
    } else {
      onStartCreating()
    }
  }, [isCreating, isEditing, disabled, onStartCreating, onStopCreating])

  const touchEnded = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (isCreating) {
      e.preventDefault()
      e.stopPropagation()
      onStopCreating()
    }
  }, [isCreating, onStopCreating])

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button && e.button !== 0) return
    touchStarted(e)
  }, [touchStarted])

  const onMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    touchEnded(e)
  }, [touchEnded])

  const onTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.changedTouches[0]
    if (touch && html.isEventOn(touch, ref.current)) {
      touchEnded(e)
    }
  }, [touchEnded])

  // Register touchstart as non-passive so preventDefault works
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const handler = (e: Event) => touchStarted(e as unknown as React.TouchEvent<HTMLDivElement>)
    el.addEventListener('touchstart', handler, { passive: false })
    return () => el.removeEventListener('touchstart', handler)
  }, [touchStarted])

  const title = isCreating
    ? 'Cancel Creating Edge'
    : `Create Connected <click drag> ${control}`

  return (
    <div
      className={cn('control', 'create-edge')}
      data-graph-type="control"
      title={title}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      ref={ref}
    >
      <a className={cn('icon', { disabled: isEditing || disabled })}>
        <span className="plus-arrow">
          <FaPlusSquare aria-hidden />
          <FaLongArrowAltLeft aria-hidden />
        </span>
        <span className="sr-only">{isCreating ? 'Stop Creating Edge' : 'Start Creating Edge'}</span>
      </a>
    </div>
  )
}

export default CreateEdgeControl
